/**
 * Recipe KIT LIST — the Subscribe & Save / one-time control and the
 * "Add the N BPN ingredients" button.
 *
 * Adds one line per request, awaited in turn. That is deliberate and it is the
 * same choice assets/section-complete-your-stack.js made for its "Add N items
 * to cart": /cart/add.js answers an `items` array the moment it is given more
 * than one line, and two shipped listeners read the add response as a single
 * line item at the top level — assets/component-cart-notification.js maps
 * `response.options_with_values`, and assets/component-data-layer.js calls
 * `id.toString()` on the same object. Both throw on the array shape. window.Cart
 * serialises mutations through its own queue and re-reads /cart.js after every
 * add, so awaiting each one in turn is also what keeps those two correct.
 *
 * Selling plans are native Skio allocations. One select drives every line, so
 * each option carries the plan id for every BPN row, in row order, separated by
 * commas, with an empty slot where that row has no plan at that frequency.
 * `plans[index]` therefore belongs to `lines[index]`; both lists are in document
 * order and the Liquid builds them from the same loop.
 *
 * Because the adds are sequential they are not atomic: a row that fails midway
 * leaves the earlier ones in the cart, and re-pressing the button used to add
 * those a second time and corrupt the kit quantities. A completed row's
 * position is now remembered on the element and skipped, so the button resumes
 * rather than restarts, and the tally is cleared after a clean run so adding
 * the kit twice on purpose still works.
 *
 * Positions rather than variant ids: a recipe may list the same variant on two
 * rows, both of which count towards the total the shopper is shown, and keying
 * on the variant would silently skip the second one.
 *
 * While a part-added kit is waiting to be resumed the purchase-mode radios and
 * the frequency select are frozen. The rows already in the cart carry the terms
 * chosen at the time, so letting the shopper switch to one-time or a different
 * frequency before pressing again would finish the kit on terms that do not
 * match the half already bought, or the total still shown on the button.
 *
 * Making this genuinely atomic means one `items` request, which needs the two
 * shipped listeners above taught the array shape first.
 *
 * @extends HTMLElement
 */
export class RecipeKitList extends HTMLElement {
  constructor() {
    super();
    this.onModeChange = this.onModeChange.bind(this);
    this.onFrequencyChange = this.onFrequencyChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (!this.submitButton) {
      return;
    }

    for (const input of this.modeInputs) {
      input.addEventListener('change', this.onModeChange);
    }
    this.select?.addEventListener('change', this.onFrequencyChange);
    this.submitButton.addEventListener('click', this.onSubmit);
    this.sync();
  }

  disconnectedCallback() {
    for (const input of this.modeInputs) {
      input.removeEventListener('change', this.onModeChange);
    }
    this.select?.removeEventListener('change', this.onFrequencyChange);
    this.submitButton?.removeEventListener('click', this.onSubmit);
  }

  get modeInputs() {
    return this.querySelectorAll('[data-recipe-kit-mode]');
  }

  get selectedMode() {
    return this.querySelector('[data-recipe-kit-mode]:checked');
  }

  get frequency() {
    return this.querySelector('[data-recipe-kit-frequency]');
  }

  get select() {
    return this.querySelector('[data-recipe-kit-select]');
  }

  get totalTarget() {
    return this.querySelector('[data-recipe-kit-total]');
  }

  get submitButton() {
    return this.querySelector('[data-recipe-kit-submit]');
  }

  get submitLabel() {
    return this.querySelector('[data-recipe-kit-submit-label]');
  }

  get submitBusy() {
    return this.querySelector('[data-recipe-kit-submit-busy]');
  }

  get errorElement() {
    return this.querySelector('[data-recipe-kit-error]');
  }

  get lines() {
    return Array.from(this.querySelectorAll('[data-recipe-kit-line]'));
  }

  /** True unless the subscribe radio exists and one-time is the chosen half. */
  get subscribing() {
    return Boolean(this.selectedMode) && this.selectedMode.value === 'subscription';
  }

  /**
   * Freezes the purchase-mode radios and the frequency select. Used while a
   * partially added kit is waiting to be resumed: the rows already in the cart
   * carry the terms chosen at the time, and letting the shopper switch to
   * one-time or another frequency before pressing again would finish the kit on
   * different terms from the half already bought.
   * @param {boolean} locked
   */
  lockPurchaseOptions(locked) {
    for (const input of this.modeInputs) {
      input.disabled = locked;
    }
    if (this.select) {
      this.select.disabled = locked;
    }
  }

  onModeChange() {
    this.sync();
  }

  onFrequencyChange() {
    this.updateTotal();
  }

  onSubmit(event) {
    event.preventDefault();
    this.addAll();
  }

  sync() {
    if (this.select) {
      this.select.disabled = !this.subscribing;
    }
    if (this.frequency) {
      this.frequency.hidden = !this.subscribing;
    }

    this.updateTotal();
  }

  /** Swaps the price inside the button label for the chosen mode's total. */
  updateTotal() {
    const target = this.totalTarget;
    if (!target) {
      return;
    }

    const total = this.subscribing
      ? this.select?.options[this.select.selectedIndex]?.dataset.recipeKitOptionTotal
      : this.selectedMode?.dataset.recipeKitModeTotal;

    if (total) {
      target.textContent = total;
    }
  }

  /** Selling plan ids for the chosen frequency, one slot per BPN row. */
  get selectedPlans() {
    if (!this.subscribing || !this.select) {
      return [];
    }

    return this.select.value.split(',');
  }

  async addAll() {
    if (!window.Cart) {
      return;
    }

    const lines = this.lines;
    if (!lines.length) {
      return;
    }

    const plans = this.selectedPlans;
    const button = this.submitButton;
    const label = this.submitLabel;
    const busy = this.submitBusy;
    const errorElement = this.errorElement;

    if (errorElement) {
      errorElement.textContent = '';
    }
    button.disabled = true;
    if (label) {
      label.hidden = true;
    }
    if (busy) {
      busy.hidden = false;
    }

    // Keyed on the row's position, not on its variant id: a recipe can list the
    // same variant twice (two separate uses of the same protein, say) and both
    // rows count towards the total the shopper is shown. Keying on the variant
    // would skip the second row on a resumed run and under-add the kit.
    if (!this.addedLineIndexes) {
      this.addedLineIndexes = new Set();
    }

    try {
      for (const [index, line] of lines.entries()) {
        if (this.addedLineIndexes.has(index)) {
          continue;
        }

        const formData = new FormData();
        formData.append('id', line.dataset.recipeKitLine);
        formData.append('quantity', '1');

        const plan = plans[index];
        if (plan) {
          formData.append('selling_plan', plan);
        }

        await window.Cart.add(formData, this);
        this.addedLineIndexes.add(index);
      }

      // A clean run: forget the tally and hand the controls back, so pressing
      // again adds the kit afresh on whatever terms the shopper then picks.
      this.addedLineIndexes.clear();
      this.lockPurchaseOptions(false);
    } catch (error) {
      if (errorElement) {
        const reason = error.description || error.message;
        const added = this.addedLineIndexes.size;
        errorElement.textContent = added > 0 ? `${reason} ${this.partialMessage(added, lines.length)}` : reason;
      }
      if (this.addedLineIndexes.size > 0) {
        this.lockPurchaseOptions(true);
      }
    } finally {
      button.disabled = false;
      if (label) {
        label.hidden = false;
      }
      if (busy) {
        busy.hidden = true;
      }
    }
  }

  /**
   * Tells the shopper how far a failed run got, so a half-added kit is visible
   * rather than silent. Copy comes from the locale file via the host element.
   * @param {number} added - lines already in the cart
   * @param {number} total - lines the button tried to add
   * @returns {string}
   */
  partialMessage(added, total) {
    const template = this.dataset.partialMessage;
    if (!template) {
      return '';
    }
    return template.replace('%added%', String(added)).replace('%count%', String(total));
  }
}

if (!customElements.get('recipe-kit-list')) {
  customElements.define('recipe-kit-list', RecipeKitList);
}
